// Reach Tool data hooks: server reads as React state, plus the fact -> pivot-row
// adapter and the extraction vocabulary labels.

import { useEffect, useState } from 'react';
import { reachApi } from './api.js';
import { MONTHS, ordinal } from './format.js';
import { PLATFORMS } from './components/ui.jsx';

/**
 * The ad accounts one CONNECTION can see — across every client that granted that email access.
 *
 * Read from the platform on every mount rather than cached: an account added in Business
 * Manager this morning has to appear without anyone clearing anything, and the list is a
 * handful of rows behind one API call. `error` is usually "that connection expired" or "you are
 * not an admin", which is why callers render it as guidance rather than as a failure.
 */
export function useAdAccounts(connectionId) {
  const [state, setState] = useState({ loading: true, accounts: [], error: '' });

  useEffect(() => {
    if (!connectionId) { setState({ loading: false, accounts: [], error: '' }); return undefined; }

    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: '' }));

    reachApi.listAdAccounts(connectionId)
      .then(rows => { if (!cancelled) setState({ loading: false, accounts: rows || [], error: '' }); })
      .catch(err => {
        if (!cancelled) setState({
          loading: false, accounts: [],
          error: err?.message || 'Ad accounts could not be read for this platform.',
        });
      });

    // Switching connection mid-request would otherwise let the slower response overwrite the
    // newer one, and the user would be picking from the wrong email's accounts.
    return () => { cancelled = true; };
  }, [connectionId]);

  return state;
}


/**
 * The agency's connections for one platform. Admin-only server-side, so a client-scoped user
 * gets an error here — which the pickers render as "ask an admin to connect one" rather than as
 * a broken screen.
 */
export function useConnections(platform) {
  const [state, setState] = useState({ loading: true, connections: [], error: '' });

  useEffect(() => {
    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: '' }));

    reachApi.listConnections(platform)
      .then(rows => {
        if (cancelled) return;
        // Only an active connection can extract. Expired and revoked rows are filtered out of
        // the pickers rather than shown and rejected on save — the Connections screen is where
        // they are meant to be seen and fixed.
        setState({ loading: false, connections: (rows || []).filter(r => r.status === 'active'), error: '' });
      })
      .catch(err => {
        if (!cancelled) setState({
          loading: false, connections: [],
          error: err?.message || 'Connections could not be read.',
        });
      });

    return () => { cancelled = true; };
  }, [platform]);

  return state;
}


// Live account -> the label shown in a chip or an option. Falls back to the id alone: an
// account with no name is still selectable, and inventing the word "Account" for it would make
// two unnamed accounts look identical.
export const liveAccountLabel = account =>
  account.name ? `${account.external_id} - ${account.name}` : account.external_id;


// Display names for the canonical extraction vocabulary. Labels are UI property; the ids and
// which of them each platform serves come from the server's capabilities endpoint, read off
// the connector registry itself. An id with no label here renders as itself rather than
// hiding — a new server-side KPI must show up, not vanish.
export const LEVEL_LABELS = { account: 'Account', campaign: 'Campaign', adset: 'Ad set', ad: 'Ad' };

export const KPI_LABELS = {
  impressions: 'Impressions', reach: 'Reach', frequency: 'Frequency', spend: 'Spend',
  clicks: 'Clicks', cpm: 'CPM', cpc: 'CPC', ctr: 'CTR',
};

export const labelForLevel = id => LEVEL_LABELS[id] || id;

export const labelForKpi = id => KPI_LABELS[id] || id;


/**
 * What one platform can extract, from the connector registry via the API. Cached for the
 * session in api.js — the registry only changes on deploy.
 *
 * Missing/unreachable capability metadata blocks saving rather than guessing which fields
 * "all" includes. The picker exposes a retry; a failed request is not cached.
 */
export function useCapabilities(platform) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    loading: true, levels: [], kpis: [], error: '', known: true,
  });

  useEffect(() => {
    if (!platform) {
      setState({ loading: false, levels: [], kpis: [], error: '', known: true });
      return undefined;
    }

    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: '' }));

    reachApi.capabilities()
      .then(map => {
        if (cancelled) return;
        const caps = map?.[platform];
        if (caps) {
          setState({ loading: false, levels: caps.levels || [], kpis: caps.kpis || [], error: '', known: true });
        } else {
          setState({ loading: false, levels: [], kpis: [], error: '', known: false });
        }
      })
      .catch(error => {
        if (!cancelled) setState({
          loading: false, levels: [], kpis: [], known: true,
          error: error?.message || 'The extraction service could not be reached.',
        });
      });

    return () => { cancelled = true; };
  }, [platform, attempt]);

  return { ...state, retry: () => setAttempt(current => current + 1) };
}


// One extracted fact row -> the shape the analysis screens group and pivot on.
//
// The date parts (year, month, quarter, ISO week) are derived here rather than stored, because
// they are presentation: the same day is week 34 or week 35 depending on the ISO rules, and
// baking that into the database would freeze one interpretation.
export function factToPivotRow(fact, streamsById) {
  const stream = streamsById[fact.connector_id] || {};
  const [year, month, day] = String(fact.date).slice(0, 10).split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day));

  // ISO-8601 week: Thursday of the same week decides the year, which is why the offset looks
  // arbitrary. Weeks 52/53 at a year boundary are wrong without it.
  const thursday = new Date(Date.UTC(year, month - 1, day));
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const weekYear = thursday.getUTCFullYear();
  const week = Math.ceil((((thursday - new Date(Date.UTC(weekYear, 0, 1))) / 86400000) + 1) / 7);

  const metrics = fact.metrics || {};
  const reach = metrics.reach == null ? null : Number(metrics.reach);
  const impressions = metrics.impressions == null ? null : Number(metrics.impressions);
  const entity = fact.entity_name || fact.entity_id;
  // Breakdowns are deliberately schema-free in bp_facts: platforms can add country,
  // placement, device or another dimension without a migration. Carry every observed scalar
  // into the analysis row under a collision-proof id. Internal flags remain metadata rather
  // than becoming user-facing dimensions.
  const breakdownDimensions = Object.fromEntries(
    Object.entries(fact.breakdowns || {})
      .filter(([key, value]) => key !== 'reach_is_approximate'
        && value !== null && value !== undefined
        && typeof value !== 'object')
      .map(([key, value]) => [`breakdown:${key}`, value]),
  );
  const parents = {};
  const parentAliases = { campaign_id: 'campaignId', campaign_name: 'campaign',
    adset_id: 'adsetId', adset_name: 'adset', ad_id: 'adId', ad_name: 'ad' };
  for (const [key, value] of Object.entries(fact.breakdowns || {})) {
    if (parentAliases[key] && value != null && typeof value !== 'object') parents[parentAliases[key]] = String(value);
  }
  const extraMetrics = Object.fromEntries(Object.entries(metrics)
    .filter(([key, value]) => !['reach', 'frequency', 'impressions', 'spend', 'clicks'].includes(key)
      && value !== null && value !== '' && typeof value !== 'object' && Number.isFinite(Number(value)))
    .map(([key, value]) => [`metric:${key}`, Number(value)]));

  return {
    id: `${fact.connector_id}-${fact.level}-${fact.entity_id}-${fact.date}`,
    dateKey: String(fact.date).slice(0, 10),
    date: `${MONTHS[month - 1]} ${ordinal(day)} ${year}`,
    year: String(year),
    month: MONTHS[month - 1],
    quarter: `Q${Math.floor((month - 1) / 3) + 1}`,
    week: `W${String(week).padStart(2, '0')}`,
    account: fact.account || fact.entity_name || fact.entity_id,
    entity,
    campaign: fact.level === 'campaign' ? entity : '',
    campaignId: fact.level === 'campaign' ? fact.entity_id : '',
    adset: fact.level === 'adset' ? entity : '',
    adsetId: fact.level === 'adset' ? fact.entity_id : '',
    ad: fact.level === 'ad' ? entity : '',
    adId: fact.level === 'ad' ? fact.entity_id : '',
    platform: PLATFORMS.find(p => p.id === fact.platform)?.label || fact.platform,
    currency: fact.currency || '',
    stream: fact.stream_name || stream.name || '',
    streamId: fact.connector_id,
    reach,
    // Carried through when the platform gave it, derived otherwise. Not defaulted to a number
    // when reach is zero: a frequency with no reach behind it is meaningless, not 0.
    frequency: metrics.frequency != null
      ? Number(metrics.frequency)
      : (reach && impressions != null ? impressions / reach : null),
    impressions,
    spend: metrics.spend != null ? Number(metrics.spend) : null,
    clicks: metrics.clicks != null ? Number(metrics.clicks) : null,
    // Reach per impression. Null rather than zero when there are no impressions, for the same
    // reason as frequency.
    efficiency: impressions && reach != null ? reach / impressions : null,
    // LinkedIn models reach rather than counting it. Carried so a view can mark it as an
    // estimate instead of presenting it as measured.
    reachIsApproximate: Boolean((fact.breakdowns || {}).reach_is_approximate),
    ...breakdownDimensions,
    ...extraMetrics,
    ...parents,
  };
}


/**
 * Extracted rows for this workspace, from bp_facts.
 *
 * Every analysis screen in this module reads through here. There is deliberately no fallback to
 * generated data: a screen with nothing to show says so, because the alternative — plausible
 * numbers next to real ones, with nothing distinguishing them — is how a fabricated figure ends
 * up in a client report.
 */
export function useFacts({ streamId, streamIds, from, to, level = 'account', granularity = 'day' } = {}) {
  const [state, setState] = useState({ loading: true, facts: [], error: '' });
  const streamKey = JSON.stringify(streamIds);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, facts: [], error: '' });

    reachApi.facts({ stream: streamId, streams: streamIds, from, to, level, granularity })
      .then(rows => { if (!cancelled) setState({ loading: false, facts: rows || [], error: '' }); })
      .catch(err => {
        if (!cancelled) setState({
          loading: false, facts: [],
          error: err?.message || 'Extracted data could not be read.',
        });
      });

    return () => { cancelled = true; };
  }, [streamId, streamKey, from, to, level, granularity]);

  return state;
}

// Pivots and exports discover the same complete source schema. Preview rows are
// bounded, but discovery on the server scans all data, including older fields.
export function useAnalysisSource(parameters) {
  const key = JSON.stringify(parameters);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ key: '', facts: [], fields: [], loading: true, error: '', dateFrom: '', dateTo: '' });
  useEffect(() => {
    let active = true;
    const empty = { key, facts: [], fields: [], dateFrom: '', dateTo: '', error: '', loading: true };
    setState(empty);
    const params = JSON.parse(key);
    if (!params.sourceStreamIds.length) { setState({ ...empty, loading: false }); return undefined; }
    reachApi.exportSource(params)
      .then(result => { if (active) setState({ ...empty, ...result, loading: false }); })
      .catch(error => { if (active) setState({ ...empty, loading: false, error: error.message || 'Available fields could not be loaded.' }); });
    return () => { active = false; };
  }, [key, attempt]);
  // Do not show the previous stream's fields even for the render before its effect runs.
  return { ...(state.key === key ? state : { facts: [], fields: [], loading: true, error: '', dateFrom: '', dateTo: '' }),
    retry: () => setAttempt(value => value + 1) };
}

